import React, { useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ChevronDown, ChevronRight, ClipboardCheck, FileUp, Loader2, Merge, PencilLine,
  Scissors, Trash2, Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import NumaSelect from "@/components/ui/numa-select";
import { theses as thesesApi, normalizeError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Bringing in work that already exists.
 *
 * Detection reads headings, numbered lines and page openings, and it is a
 * guess: a chapter title set in the same face as the body text is invisible to
 * it, and a page that opens with a quotation looks exactly like a new chapter.
 * So the guess is shown before anything is created, and it can be corrected -
 * renamed, merged, split, dropped - because the author knows the structure of
 * their own document and NUMA does not.
 *
 * Nothing here uses a model. It is heading detection, which is arithmetic.
 */

const PROJECT_TYPES = [
  { value: "thesis", label: "Tesi" },
  { value: "volume", label: "Volume" },
  { value: "article", label: "Articolo scientifico" },
  { value: "review", label: "Rassegna" },
  { value: "research", label: "Progetto di ricerca" },
];

export default function ImportThesisModal({ open, onOpenChange, onImported, onProjectLimit }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [step, setStep] = useState("idle"); // idle | reading | review | creating
  // Quante pagine sono state lette: su un documento lungo l'attesa è di
  // minuti, e una rotella che gira non dice se sta succedendo qualcosa.
  const [avanzamento, setAvanzamento] = useState(null);
  const [preview, setPreview] = useState(null);
  const [groups, setGroups] = useState([]);
  const [title, setTitle] = useState("");
  const [projectType, setProjectType] = useState("thesis");
  const [projectMode, setProjectMode] = useState("write");
  const [expanded, setExpanded] = useState({});

  /*
    Una lettura alla volta, e quella in corso è sempre l'ultima chiesta.

    Chi annulla di solito ha sbagliato file: la lettura di prima non deve
    continuare - né sul server, dove consuma la macchina, né qui, dove
    continuava a scrivere il suo avanzamento sopra quello nuovo. Il numero di
    giro dice qual è la lettura buona; le altre si fermano da sole.
  */
  const giro = useRef(0);

  const reset = () => {
    // Chiudere la finestra è un modo di annullare: il giro cambia, e la
    // lettura in corso - qui e sul server - si accorge di non servire più.
    giro.current += 1;
    setAvanzamento(null);
    setStep("idle");
    setPreview(null);
    setGroups([]);
    setTitle("");
    setProjectType("thesis");
    setProjectMode("write");
    setExpanded({});
  };

  const sectionsByIndex = useMemo(() => {
    const map = {};
    for (const section of preview?.sections || []) map[section.index] = section;
    return map;
  }, [preview]);

  const wordsOf = (group) =>
    group.indexes.reduce((sum, i) => sum + (sectionsByIndex[i]?.words || 0), 0);

  const read = async (file) => {
    if (!file) return;
    if (!/\.(pdf|docx)$/i.test(file.name)) {
      toast.error("Solo PDF o DOCX");
      return;
    }
    const mio = ++giro.current;
    const ancoraValida = () => giro.current === mio;
    setAvanzamento(null);
    setStep("reading");
    try {
      const res = await thesesApi.importPreview(
        file,
        (a) => { if (ancoraValida()) setAvanzamento(a); },
        ancoraValida,
      );
      if (!ancoraValida()) return;
      setPreview(res);
      setGroups((res.sections || []).map((s) => ({ title: s.title, indexes: [s.index] })));
      setTitle(file.name.replace(/\.(pdf|docx)$/i, ""));
      setStep("review");
    } catch (e) {
      if (e?.annullata || !ancoraValida()) return;   // l'ha fermata l'autore
      const err = normalizeError(e);
      toast.error("Lettura non riuscita", { description: err.message });
      setStep("idle");
    }
  };

  const rename = (position, value) =>
    setGroups((list) => list.map((g, i) => (i === position ? { ...g, title: value } : g)));

  /** Fold a section into the one above it: the text stays, the split goes. */
  const mergeUp = (position) =>
    setGroups((list) => {
      if (position === 0) return list;
      const next = [...list];
      next[position - 1] = {
        ...next[position - 1],
        indexes: [...next[position - 1].indexes, ...next[position].indexes],
      };
      next.splice(position, 1);
      return next;
    });

  /** Undo a merge, back to the pieces detection had found. */
  const split = (position) =>
    setGroups((list) => {
      const group = list[position];
      if (group.indexes.length < 2) return list;
      const pieces = group.indexes.map((index) => ({
        title: sectionsByIndex[index]?.title || "Sezione",
        indexes: [index],
      }));
      const next = [...list];
      next.splice(position, 1, ...pieces);
      return next;
    });

  const drop = (position) =>
    setGroups((list) => list.filter((_, i) => i !== position));

  const create = async () => {
    setStep("creating");
    try {
      const res = await thesesApi.importConfirm({
        draft_id: preview.draft_id,
        title: title.trim() || "Progetto importato",
        project_type: projectType,
        project_mode: projectMode,
        groups: groups.map((g) => ({ title: g.title, indexes: g.indexes })),
      });
      toast.success(`Progetto creato con ${res.chapters} sezioni`);
      onImported?.(res.thesis);
      onOpenChange(false);
      reset();
    } catch (e) {
      const err = normalizeError(e);
      if (err.code === "project_limit") {
        // Spiegato dal pannello che il workspace apre, non da un avviso che
        // scompare mentre lo si legge.
        onOpenChange(false);
        onProjectLimit?.();
        return;
      }
      toast.error("Creazione non riuscita", { description: err.message });
      setStep("review");
    }
  };

  const droppedWords =
    (preview?.total_words || 0) - groups.reduce((sum, g) => sum + wordsOf(g), 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="import-modal" className="max-w-[95vw] sm:max-w-2xl rounded-md">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">
            {step === "review" ? "Controlla la struttura" : "Importa un lavoro già iniziato"}
          </DialogTitle>
        </DialogHeader>

        {step === "idle" && (
          <div
            data-testid="dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); read(e.dataTransfer.files?.[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`mt-2 cursor-pointer rounded-md border-2 border-dashed p-7 text-center transition-all sm:p-12 ${
              dragOver ? "border-brand bg-assistant-soft" : "border-line bg-paper hover:bg-surface-2/50"
            }`}
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-md border border-line bg-surface shadow-sm">
              <FileUp className="h-7 w-7 text-assistant" />
            </div>
            <p className="font-serif-display text-lg font-semibold text-ink">Trascina qui il tuo file</p>
            <p className="mx-auto mt-2 max-w-sm text-sm text-ink-muted">
              PDF o DOCX. NUMA ne riconosce capitoli e paragrafi e te li mostra prima di
              creare il progetto, così puoi correggerli.
            </p>
            {/* Dichiarato prima, non dopo: un limite che si scopre a caricamento
                finito è una perdita di tempo, non una regola. */}
            <p className="mx-auto mt-1.5 max-w-sm text-2xs text-ink-subtle">
              Fino a 1000 pagine e 40 MB per file. Un documento più lungo va diviso —
              per esempio un volume per volta.
            </p>
            <Button data-testid="choose-file-btn" className="mt-5 rounded-md bg-brand hover:bg-brand-hover">
              <Upload className="mr-2 h-4 w-4" /> Scegli un file
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.docx"
              className="hidden"
              onChange={(e) => read(e.target.files?.[0])}
            />
          </div>
        )}

        {step === "reading" && (
          <div className="py-10 text-center" data-testid="import-uploading">
            <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-assistant" />
            <p className="text-sm font-medium text-ink">
              {avanzamento?.pages
                ? `Lettura del documento: pagina ${avanzamento.done} di ${avanzamento.pages}`
                : "Lettura del documento…"}
            </p>
            {avanzamento?.pages ? (
              <div className="mx-auto mt-3 h-1.5 w-56 overflow-hidden rounded-full bg-surface-2">
                <div
                  className="h-full bg-assistant transition-[width] duration-500"
                  style={{ width: `${Math.min(100, Math.round((avanzamento.done / Math.max(1, avanzamento.pages)) * 100))}%` }}
                />
              </div>
            ) : null}
            <p className="mt-3 text-xs text-ink-muted">
              NUMA sta cercando titoli di capitolo e di paragrafo. Nessun modello, nessun costo.
              <br />
              Puoi lasciare la finestra aperta: un documento di trecento pagine richiede
              qualche minuto.
            </p>
          </div>
        )}

        {(step === "review" || step === "creating") && preview && (
          <div data-testid="import-review">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <div className="space-y-1.5">
                <Label htmlFor="import-title">Titolo del progetto</Label>
                <Input
                  id="import-title"
                  data-testid="import-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="import-type">Tipo</Label>
                <NumaSelect
                  id="import-type"
                  testId="import-type"
                  value={projectType}
                  onValueChange={setProjectType}
                  className="h-9 min-w-[11rem] text-[13px]"
                  options={PROJECT_TYPES}
                />
              </div>
            </div>

            {/* What the document is here for. A finished manuscript and a draft
                being continued need different halves of NUMA, and asking now
                is cheaper than letting someone find the wrong half later. */}
            <fieldset className="mt-4">
              <legend className="mb-2 text-sm font-medium text-ink">
                Cosa vuoi fare con questo documento?
              </legend>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {[
                  {
                    id: "write",
                    icon: PencilLine,
                    label: "Continuare a scriverlo",
                    hint: "Editor, scalette, avanzamento: NUMA resta completo.",
                  },
                  {
                    id: "review",
                    icon: ClipboardCheck,
                    label: "Solo una revisione",
                    hint: "È finito: forma, lingua e merito, con le correzioni da accogliere o scaricare.",
                  },
                ].map((option) => {
                  const Icon = option.icon;
                  const active = projectMode === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      data-testid={`import-mode-${option.id}`}
                      onClick={() => setProjectMode(option.id)}
                      aria-pressed={active}
                      className={`flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-left transition-colors ${
                        active ? "border-brand bg-brand-soft" : "border-line-strong hover:bg-surface-2"
                      }`}
                    >
                      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${active ? "text-brand" : "text-ink-muted"}`} />
                      <span className="min-w-0">
                        <span className={`block text-[13.5px] font-medium ${active ? "text-brand" : "text-ink"}`}>
                          {option.label}
                        </span>
                        <span className="mt-0.5 block text-2xs leading-snug text-ink-muted">
                          {option.hint}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <p className="numa-meta mt-4">
              {groups.length} sezioni da <strong className="text-ink">{preview.filename}</strong>.
              Rinomina quelle riconosciute male, unisci quelle spezzate a metà, separa quelle
              accorpate per errore.
            </p>

            <div className="mt-3 max-h-[46vh] space-y-1.5 overflow-y-auto pr-1">
              {groups.map((group, position) => {
                const pieces = group.indexes.map((i) => sectionsByIndex[i]).filter(Boolean);
                const isOpen = expanded[position];
                // Capitoli e paragrafi arrivano in un elenco solo: il rientro
                // dice quali sono i paragrafi di quale capitolo, ed è il modo
                // più rapido per accorgersi che il documento è stato letto
                // bene - o per unire due pezzi che andavano insieme.
                const livello = Math.min(Math.max(Number(pieces[0]?.level) || 1, 1), 4);
                return (
                  <div
                    key={`${group.indexes.join("-")}-${position}`}
                    style={{ marginLeft: `${(livello - 1) * 18}px` }}
                    className="rounded-md border border-line bg-surface px-3 py-2.5"
                    data-testid={`import-section-${position}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-5 shrink-0 text-2xs tabular-nums text-ink-subtle">
                        {position + 1}
                      </span>
                      <Input
                        value={group.title}
                        onChange={(e) => rename(position, e.target.value)}
                        aria-label={`Titolo della sezione ${position + 1}`}
                        className="h-8 flex-1 text-[13.5px]"
                      />
                      <span className="shrink-0 text-2xs tabular-nums text-ink-subtle">
                        {wordsOf(group)} parole
                      </span>
                      <div className="flex shrink-0 gap-0.5">
                        <button
                          type="button"
                          title="Unisci alla sezione precedente"
                          onClick={() => mergeUp(position)}
                          disabled={position === 0}
                          className="rounded p-1.5 text-ink-subtle hover:bg-surface-2 hover:text-ink disabled:opacity-30"
                        >
                          <Merge className="h-3.5 w-3.5" />
                          <span className="sr-only">Unisci alla precedente</span>
                        </button>
                        <button
                          type="button"
                          title="Separa di nuovo le parti unite"
                          onClick={() => split(position)}
                          disabled={group.indexes.length < 2}
                          className="rounded p-1.5 text-ink-subtle hover:bg-surface-2 hover:text-ink disabled:opacity-30"
                        >
                          <Scissors className="h-3.5 w-3.5" />
                          <span className="sr-only">Separa</span>
                        </button>
                        <button
                          type="button"
                          title="Non importare questa sezione"
                          onClick={() => drop(position)}
                          className="rounded p-1.5 text-ink-subtle hover:bg-danger/10 hover:text-danger"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="sr-only">Escludi</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpanded((e) => ({ ...e, [position]: !e[position] }))}
                          className="rounded p-1.5 text-ink-subtle hover:bg-surface-2 hover:text-ink"
                        >
                          {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          <span className="sr-only">Anteprima del testo</span>
                        </button>
                      </div>
                    </div>
                    {isOpen && (
                      <div className="mt-2 space-y-1.5 border-t border-line pt-2">
                        {pieces.map((piece) => (
                          <p key={piece.index} className="text-2xs leading-relaxed text-ink-muted">
                            <span className="text-ink-subtle">{piece.title}: </span>
                            {piece.snippet || "(nessun testo)"}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {droppedWords > 0 && (
              <p className="mt-3 rounded-md border border-warn/40 bg-warn/5 px-3 py-2 text-2xs text-ink-muted">
                {droppedWords} parole resteranno fuori dal progetto perché hai escluso alcune
                sezioni. Il file originale non viene modificato.
              </p>
            )}

            <div className="mt-5 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={reset}
                className="text-[12.5px] text-ink-muted hover:text-ink hover:underline"
              >
                Cambia file
              </button>
              <Button
                data-testid="import-confirm-btn"
                onClick={create}
                disabled={step === "creating" || groups.length === 0}
                className="rounded-md bg-brand hover:bg-brand-hover"
              >
                {step === "creating" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Crea il progetto
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
